'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { CircleAlert, Pipette } from 'lucide-react';
import styles from './ColorField.module.css';

export interface ColorFieldProps {
  /**
   * Label text
   */
  label?: string;

  /**
   * Hex colour value, e.g. `#13319a`. Empty string renders the unset state.
   */
  value: string;

  /**
   * Called with a normalised `#rrggbb` value, or `''` when cleared.
   */
  onChange: (value: string) => void;

  /** Called with the normalized value when the hex input loses focus. */
  onBlur?: (value: string) => void;

  /**
   * Helper/description text
   */
  helperText?: string;

  /**
   * Error message (also sets invalid state)
   */
  error?: string;

  disabled?: boolean;

  /** Saved value restored by the picker’s revert action. */
  originalValue?: string;

  /**
   * Additional class names for the root element
   */
  className?: string;

  /** Fires with `true` right before the native eyedropper opens and `false`
   * once it settles (pick or cancel) — lets a modal container temporarily
   * relax its scroll lock so the user can scroll the page into view of
   * whatever they want to sample (e.g. a logo above the fold). */
  onPickingChange?: (picking: boolean) => void;
}

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

interface HsvColor {
  h: number;
  s: number;
  v: number;
}

interface EyeDropperConstructor {
  new (): {
    open: () => Promise<{ sRGBHex: string }>;
  };
}

/** Add the conventional hash to a complete bare hex value. */
function prefixBareHex(hex: string): string {
  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex}` : hex;
}

/** Expand `#abc` to `#aabbcc`; leave anything else untouched. */
function expandShorthand(hex: string): string {
  const bare = hex.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$/.test(bare)) return hex;
  return `#${bare[0]}${bare[0]}${bare[1]}${bare[1]}${bare[2]}${bare[2]}`;
}

function hexToHsv(hex: string): HsvColor {
  const number = Number.parseInt(hex.slice(1), 16);
  const red = ((number >> 16) & 255) / 255;
  const green = ((number >> 8) & 255) / 255;
  const blue = (number & 255) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (max === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }

  return {
    h: hue < 0 ? hue + 360 : hue,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

function hsvToHex({ h, s, v }: HsvColor): string {
  const chroma = v * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const offset = v - chroma;
  const [red, green, blue] =
    h < 60 ? [chroma, x, 0] :
    h < 120 ? [x, chroma, 0] :
    h < 180 ? [0, chroma, x] :
    h < 240 ? [0, x, chroma] :
    h < 300 ? [x, 0, chroma] : [chroma, 0, x];
  const channel = (value: number) =>
    Math.round((value + offset) * 255).toString(16).padStart(2, '0');

  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

/**
 * ColorField Component
 *
 * A hex colour input paired with a native colour-picker swatch. The swatch and
 * the text field stay in sync: typing a valid hex moves the picker, and the
 * picker writes back a normalised `#rrggbb`.
 *
 * Laws:
 * - Matches Input's height, radius and border treatment
 * - The swatch is the affordance; the hex is for precision and pasting
 * - Invalid input is shown, not swallowed — the user's keystrokes are theirs
 */
export function ColorField({
  label,
  value,
  onChange,
  onBlur,
  helperText,
  error,
  disabled,
  originalValue,
  className,
  onPickingChange,
}: ColorFieldProps) {
  const inputId = React.useId();
  const normalised = expandShorthand(prefixBareHex((value || '').trim()));
  const isValid = HEX_PATTERN.test(normalised);
  const pickerValue = isValid ? normalised.toLowerCase() : '#000000';
  const normalisedOriginal = expandShorthand(
    prefixBareHex((originalValue || '').trim())
  ).toLowerCase();
  const canRevert =
    originalValue !== undefined && normalised.toLowerCase() !== normalisedOriginal;
  const hasError = Boolean(error);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pickerAbove, setPickerAbove] = React.useState(false);
  const pickerRootRef = React.useRef<HTMLDivElement>(null);
  const hsv = hexToHsv(pickerValue);

  // Roughly the popup's own rendered height (saturation square + hue slider +
  // eyedropper button + padding) — used to decide whether it has room to drop
  // down or needs to flip above the field instead.
  const ESTIMATED_PICKER_HEIGHT = 340;

  const updatePickerPosition = React.useCallback(() => {
    const rect = pickerRootRef.current?.getBoundingClientRect();
    const spaceBelow = rect ? window.innerHeight - rect.bottom : Infinity;
    setPickerAbove(spaceBelow < ESTIMATED_PICKER_HEIGHT);
  }, [setPickerAbove]);

  const togglePicker = () => {
    const willOpen = !pickerOpen;
    if (willOpen) updatePickerPosition();
    setPickerOpen(willOpen);
  };

  // Re-checks on every scroll (capture phase, so it also catches scrolling
  // inside a nested container like the sheet's own scrollable body — scroll
  // events don't bubble, but they do trickle through the capture phase) so
  // the popup keeps flipping to whichever side actually has room as the page
  // scrolls, instead of freezing wherever it first opened.
  React.useEffect(() => {
    if (!pickerOpen) return;

    window.addEventListener('scroll', updatePickerPosition, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', updatePickerPosition);
    return () => {
      window.removeEventListener('scroll', updatePickerPosition, true);
      window.removeEventListener('resize', updatePickerPosition);
    };
  }, [pickerOpen, updatePickerPosition]);

  React.useEffect(() => {
    if (!pickerOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!pickerRootRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [pickerOpen]);

  const selectSaturationAndBrightness = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const saturation = Math.min(
      1,
      Math.max(0, (event.clientX - bounds.left) / bounds.width)
    );
    const brightness = Math.min(
      1,
      Math.max(0, 1 - (event.clientY - bounds.top) / bounds.height)
    );
    onChange(hsvToHex({ h: hsv.h, s: saturation, v: brightness }));
  };

  const adjustSaturationAndBrightness = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    const step = event.shiftKey ? 0.1 : 0.02;
    const next = { ...hsv };
    if (event.key === 'ArrowLeft') next.s = Math.max(0, hsv.s - step);
    else if (event.key === 'ArrowRight') next.s = Math.min(1, hsv.s + step);
    else if (event.key === 'ArrowDown') next.v = Math.max(0, hsv.v - step);
    else if (event.key === 'ArrowUp') next.v = Math.min(1, hsv.v + step);
    else return;

    event.preventDefault();
    onChange(hsvToHex(next));
  };

  const pickColourFromPage = async () => {
    const EyeDropper = (
      window as Window & { EyeDropper?: EyeDropperConstructor }
    ).EyeDropper;
    if (!EyeDropper) return;

    onPickingChange?.(true);
    try {
      const result = await new EyeDropper().open();
      onChange(result.sRGBHex.toLowerCase());
    } catch {
      // Closing the eyedropper without choosing a colour is a normal cancel.
    } finally {
      onPickingChange?.(false);
    }
  };

  return (
    <div
      className={clsx(styles.root, className)}
      onBlurCapture={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
          onBlur?.(normalised);
        }
      }}
    >
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}

      <div
        className={clsx(
          styles.control,
          hasError && styles.invalid,
          disabled && styles.disabled
        )}
      >
        <div className={styles.swatchWrapper} ref={pickerRootRef}>
          <button
            type="button"
            className={clsx(styles.swatch, !isValid && styles.swatchEmpty)}
            disabled={disabled}
            aria-label={label ? `Choose ${label.toLowerCase()}` : 'Choose colour'}
            aria-expanded={pickerOpen}
            onClick={togglePicker}
            style={isValid ? { backgroundColor: pickerValue } : undefined}
          />

          {pickerOpen && (
            <div
              className={clsx(styles.picker, pickerAbove && styles.pickerAbove)}
              role="dialog"
              aria-label="Colour picker"
            >
              <div
                className={styles.saturation}
                style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
                role="slider"
                tabIndex={0}
                aria-label="Choose saturation and brightness"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(hsv.s * 100)}
                aria-valuetext={`${Math.round(hsv.s * 100)}% saturation, ${Math.round(hsv.v * 100)}% brightness`}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  selectSaturationAndBrightness(event);
                }}
                onPointerMove={(event) => {
                  if (event.buttons === 1) selectSaturationAndBrightness(event);
                }}
                onKeyDown={adjustSaturationAndBrightness}
              >
                <span
                  className={styles.saturationMarker}
                  style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
                />
              </div>
              <input
                type="range"
                className={styles.hue}
                min="0"
                // hexToHsv can return a hue up to 360 (e.g. near-magenta reds),
                // which wraps to red just like 0 — a max of 359 would clamp the
                // thumb away from the true hue at that boundary.
                max="360"
                value={Math.round(hsv.h)}
                aria-label="Hue"
                onChange={(event) =>
                  onChange(hsvToHex({ ...hsv, h: Number(event.target.value) }))
                }
              />
              <button
                type="button"
                className={styles.eyedropper}
                onClick={pickColourFromPage}
              >
                <Pipette size={16} aria-hidden="true" />
                Pick colour from page
              </button>
              <button
                type="button"
                className={styles.revert}
                disabled={!canRevert}
                onClick={() => onChange(originalValue ?? '')}
              >
                Revert to original
              </button>
            </div>
          )}
        </div>

        <input
          id={inputId}
          type="text"
          className={styles.hex}
          value={value ?? ''}
          disabled={disabled}
          placeholder="#000000"
          spellCheck={false}
          autoComplete="off"
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => {
            const expanded = expandShorthand(
              prefixBareHex(event.target.value.trim())
            );
            if (expanded !== event.target.value) onChange(expanded);
            onBlur?.(expanded);
          }}
        />
      </div>

      {error && (
        <div id={`${inputId}-error`} className={styles.error} role="alert">
          <CircleAlert size={14} className={styles.errorIcon} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {helperText && !error && (
        <p id={`${inputId}-helper`} className={styles.helper}>{helperText}</p>
      )}
    </div>
  );
}

ColorField.displayName = 'ColorField';

export default ColorField;
